import type { ChecklistItem, ProjectNode } from '@/shared/types/projectData';
import { apiClient } from '@/shared/apiClient';
import type { HandlerDependencies } from './TaskDetailsPanelHandlers';
import { buildUpdateUndoData } from '@/shared/commands/handlers/nodeOperationHandlers';
import { getNodeId, getProjectIdFromNodeId } from '@/features/projects/utils/projectUtils';
import { ConfirmedWriteError } from '@/features/projects/contexts/ProjectDataContext/types';
import { conditionalContentFields as undoableFields } from '@/shared/utils/conditionalNodeUpdate';

type PanelUndo = { before: Partial<ProjectNode>; after: Partial<ProjectNode> };
// These content fields have sparse inverses without lifecycle/subtree side effects.
// Structural, lifecycle, checklist and authority commands retain their own history.
const equalField = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

export interface PanelPersistenceState {
  undo?: PanelUndo;
  undoRequested?: PanelUndo;
  key: string;
  activeFields: Partial<ProjectNode>;
  activeVersions: Record<string, number>;
  listeners: Set<() => void>;
  tail: Promise<void>;
  pending: number;
  fields: Partial<ProjectNode>;
  progressAtomic?: boolean;
  revision: number;
  fieldVersions: Record<string, number>;
  submittedVersions: Record<string, number>;
  error: string | null;
  checklist?: { saved: ChecklistItem[]; desired: ChecklistItem[]; ids: Map<string, string>; uncertainCreate?: boolean };
}
export type PanelPersistenceRef = { current: PanelPersistenceState | null };
// Owner identity survives panel and auth-session remounts; execution authority does not.
// Recovery is tab-runtime state. Never retain component setters or transport closures here.
const owners = new Map<string, PanelPersistenceState>();
const fallback = new WeakMap<HandlerDependencies['setEditedTask'], PanelPersistenceRef>();
const taskKey = (task: Partial<ProjectNode>) => JSON.stringify([
  task.projectId || (task.id && getProjectIdFromNodeId(task.id, undefined)) || '', getNodeId(task.id || '')]);
const createState = (key: string): PanelPersistenceState => ({ key, tail: Promise.resolve(), pending: 0,
  fields: {}, activeFields: {}, activeVersions: {}, listeners: new Set(), revision: 0, fieldVersions: {}, submittedVersions: {}, error: null });
export function getPanelOwner(ownerKey: string, task: Partial<ProjectNode>) {
  const key = JSON.stringify([ownerKey, taskKey(task)]);
  let state = owners.get(key);
  if (!state) { state = createState(key); owners.set(key, state); }
  return state;
}
export const notifyPanelOwner = (state: PanelPersistenceState) => state.listeners.forEach(listener => listener());
export const panelChecklistPending = (state: PanelPersistenceState) => !!(state.checklist && (state.checklist.uncertainCreate || !sameChecklist(state.checklist.saved, state.checklist.desired)));
export const panelHasIntent = (state: PanelPersistenceState) => Object.keys(state.fields).length > 0
  || Object.keys(state.activeFields).length > 0 || panelChecklistPending(state) || !!state.undoRequested;
export function markPanelIntent(deps: HandlerDependencies, changes: Partial<ProjectNode>) {
  const { state } = persistenceState(deps);
  const revision = ++state.revision;
  Object.assign(state.activeFields, changes);
  for (const field of Object.keys(changes)) state.activeVersions[field] = revision;
}
export function endPanelIntent(deps: HandlerDependencies, field: keyof ProjectNode) {
  const { state } = persistenceState(deps);
  delete state.activeFields[field]; delete state.activeVersions[field];
}
export function persistenceState(deps: HandlerDependencies) {
  let ref = deps.persistenceRef || fallback.get(deps.setEditedTask);
  if (!ref) { ref = { current: null }; fallback.set(deps.setEditedTask, ref); }
  const task = deps.getEditedTask?.() || deps.editedTask;
  const key = taskKey(task);
  if (deps.ownerKey && task.id) ref.current = getPanelOwner(deps.ownerKey, task);
  else if (!ref.current || ref.current.key !== key) ref.current = createState(key);
  const state = ref.current;
  // Each enqueue receives the caller's captured session guard, including on retry.
  const ownsSession = deps.ownsSession;
  const owns = () => ownsSession?.() ?? true;
  const canRender = () => owns() && ref.current === state && (deps.isPanelMounted?.() ?? true);
  return { state, owns, canRender };
}
function report(deps: HandlerDependencies, state: PanelPersistenceState, canRender: () => boolean, error: unknown) {
  state.error = error instanceof Error ? error.message : deps.t('Failed to save changes');
  if (canRender()) {
    deps.setHasChanges(true);
    deps.setSaveError?.(state.error);
  }
}
function enqueue(deps: HandlerDependencies, work: () => Promise<void>) {
  const { state, owns, canRender } = persistenceState(deps);
  state.pending++;
  state.tail = state.tail.then(async () => {
    if (!owns()) throw new Error('Session changed. Return to the original account and retry pending changes.');
    await work();
    if (!owns()) throw new Error('Session changed. Confirm pending changes from the original account.');
  }).catch(error => report(deps, state, canRender, error)).finally(() => { state.pending--; notifyPanelOwner(state); });
  return state.tail;
}
export function persistPanelFields(deps: HandlerDependencies, changes: Partial<ProjectNode>, progress = false) {
  if (deps.ownsSession?.() === false || !deps.canUpdateTask) return Promise.resolve();
  const task = { ...(deps.getEditedTask?.() || deps.editedTask) };
  if (!task.id) return Promise.resolve();
  const project = (node: Partial<ProjectNode>) => node.projectId || (node.id && getProjectIdFromNodeId(node.id, undefined));
  const projectId = project(task);
  const targetId = projectId && projectId !== 'default' ? `${projectId}:${getNodeId(task.id)}` : task.id;
  const { state, owns, canRender } = persistenceState(deps);
  Object.assign(state.fields, changes);
  const revision = ++state.revision;
  for (const field of Object.keys(changes)) {
    state.fieldVersions[field] = revision;
    delete state.activeFields[field as keyof ProjectNode]; delete state.activeVersions[field];
  }
  if ('progress' in changes) state.progressAtomic = progress;
  return enqueue(deps, async () => {
    // Capture the server baseline, not a stale task prop or an optimistic draft.
    // A failed optional history read must not prevent the existing save/retry path.
    let before: Partial<ProjectNode> | undefined;
    if (!progress && Object.keys(changes).every(field => undoableFields.has(field)) && deps.readSavedTask && projectId) {
      before = await deps.readSavedTask(projectId, getNodeId(task.id!)).catch(() => undefined);
      if (!owns()) return;
      if (before && (!before.id || taskKey(before) !== taskKey(task))) before = undefined;
    }
    if (progress && typeof changes.progress === 'number') await deps.updateProgress(targetId, changes.progress);
    else await deps.updateNode(targetId, changes);
    if (!owns()) return;
    for (const field of Object.keys(changes)) state.submittedVersions[field] = revision;
    // A fresh read is the only confirmation available from a void context write.
    if (deps.readSavedTask) {
      if (!projectId) throw new Error('Cannot confirm changes without a project.');
      const confirmed = await deps.readSavedTask(projectId, getNodeId(task.id!));
      if (!owns()) return;
      if (!confirmed.id || getNodeId(confirmed.id) !== getNodeId(task.id!) || project(confirmed) !== project(task)) {
        throw new Error('Saved task identity did not match the pending changes.');
      }
      if (before && Object.keys(changes).every(field => equalField(confirmed[field as keyof ProjectNode], changes[field as keyof ProjectNode]))) {
        const history = state.undo ||= { before: {}, after: {} };
        for (const field of Object.keys(changes) as (keyof ProjectNode)[]) {
          if (equalField(before[field], confirmed[field])) continue;
          // A remote edit between two panel saves starts a new baseline for that field.
          if (!(field in history.after) || !equalField(history.after[field], before[field])) {
            Object.assign(history.before, { [field]: structuredClone(before[field]) });
          }
          Object.assign(history.after, { [field]: structuredClone(confirmed[field]) });
          if (equalField(history.before[field], history.after[field])) {
            delete history.before[field]; delete history.after[field];
          }
        }
        if (!Object.keys(history.after).length) state.undo = undefined;
      }
      for (const field of Object.keys(changes) as (keyof ProjectNode)[]) {
        if (state.fieldVersions[field] !== revision) continue;
        if (JSON.stringify(confirmed[field]) !== JSON.stringify(state.fields[field])) continue;
        delete state.fields[field];
        delete state.fieldVersions[field];
        delete state.submittedVersions[field];
      }
      const checklistPending = state.checklist && (state.checklist.uncertainCreate || !sameChecklist(state.checklist.saved, state.checklist.desired));
      if (!Object.keys(state.fields).length && !checklistPending && state.pending === 1) {
        state.error = null;
        if (canRender()) {
          deps.setSaveError?.(null);
          if (!deps.titleDraftStateRef.current.active) deps.setHasChanges(false);
        }
      } else if (Object.keys(changes).some(field => state.fieldVersions[field] === revision)) {
        throw new Error('Changes are not yet confirmed by the server. Retry or reload to check saved state.');
      }
    }
    // Without a read contract, an offline enqueue remains unconfirmed.
  });
}
/** Undo is a compensating field update through the normal command/journal path.
 * It never restores a whole node or invokes a global multi-node history group.
 */
export function undoPanelFields(deps: HandlerDependencies) {
  const { state, owns, canRender } = persistenceState(deps);
  if (!owns() || !deps.canUpdateTask || !canRender()) return Promise.resolve();
  if (state.pending || Object.keys(state.fields).length || Object.keys(state.activeFields).length || panelChecklistPending(state)) {
    report(deps, state, canRender, new Error(deps.t('Changes may already be saved. Retry pending changes or reload to confirm saved state.')));
    return Promise.resolve();
  }
  const history = state.undoRequested || state.undo;
  if (!history || !deps.readSavedTask) return Promise.resolve();
  const task = { ...(deps.getEditedTask?.() || deps.editedTask) };
  const projectId = task.projectId || (task.id && getProjectIdFromNodeId(task.id, undefined));
  if (!task.id || !projectId || projectId === 'default') return Promise.resolve();
  const inverse = buildUpdateUndoData({ data: history.after, beforeState: history.before });
  const fields = Object.keys(inverse) as (keyof ProjectNode)[];
  const recovering = !!state.undoRequested;
  state.undoRequested = history;
  state.error = null;
  notifyPanelOwner(state);
  const read = async () => {
    const saved = await deps.readSavedTask!(projectId, getNodeId(task.id!));
    if (!owns()) throw new Error('Session changed. Confirm Undo from the original account.');
    if (!saved.id || taskKey(saved) !== taskKey(task)) throw new Error('Saved task identity did not match Undo.');
    return saved;
  };
  return enqueue(deps, async () => {
    const saved = await read();
    if (!canRender()) throw new Error('Panel changed. Return to this task to confirm Undo.');
    const alreadyUndone = recovering && fields.every(field => equalField(saved[field], inverse[field]));
    if (!alreadyUndone) {
      if (fields.some(field => !equalField(saved[field], history.after[field]))) {
        // No write was sent in this attempt. Keep history for an explicit retry,
        // but permit editing after a known conflict on a first Undo attempt.
        if (!recovering) state.undoRequested = undefined;
        throw new Error('Task changed since these edits. Undo was not applied. Refresh to review the saved fields.');
      }
      try {
        await deps.updateNode(`${projectId}:${getNodeId(task.id!)}`, inverse, {
          expectedFields: structuredClone(history.after),
        });
      } catch (error) {
        // A definite refusal leaves no in-flight write from this first attempt.
        // Recovery of an earlier unknown outcome still needs authoritative readback.
        if (!recovering && error instanceof ConfirmedWriteError && error.outcome !== 'unknown') {
          state.undoRequested = undefined;
        }
        throw error;
      }
    }
    if (!owns()) return;
    const confirmed = alreadyUndone ? saved : await read();
    if (fields.some(field => !equalField(confirmed[field], inverse[field]))) {
      throw new Error('Undo is not yet confirmed by the server. Retry to check saved state.');
    }
    state.undoRequested = undefined;
    state.undo = undefined;
    state.error = null;
    if (canRender()) {
      deps.setEditedTask(previous => ({ ...previous, ...Object.fromEntries(fields.map(field => [field, confirmed[field]])) }));
      deps.setHasChanges(false);
      deps.setSaveError?.(null);
      if ('title' in inverse) {
        deps.lastCommittedTitleRef.current = confirmed.title || '';
        deps.pendingTitleAckRef.current = null;
      }
    }
  });
}

export function retryPanelPersistence(deps: HandlerDependencies) {
  const { state } = persistenceState(deps);
  if (deps.ownsSession?.() === false || !deps.canUpdateTask) return Promise.resolve();
  if (state.undoRequested || (state.undo && state.error && !panelHasIntent(state))) return undoPanelFields(deps);
  const fields = Object.fromEntries(Object.entries(state.fields).filter(([field]) => !(field in state.activeVersions))) as Partial<ProjectNode>;
  if (state.progressAtomic && typeof fields.progress === 'number') {
    void persistPanelFields(deps, { progress: fields.progress }, true);
    delete fields.progress;
  }
  if (Object.keys(fields).length) void persistPanelFields(deps, fields);
  if (state.checklist) void persistPanelChecklist(deps, state.checklist.saved, state.checklist.desired);
  return state.tail;
}
export function persistPanelChecklist(deps: HandlerDependencies, previous: ChecklistItem[], desired: ChecklistItem[]) {
  if (deps.ownsSession?.() === false || !deps.canUpdateTask) return Promise.resolve();
  const task = { ...(deps.getEditedTask?.() || deps.editedTask) };
  const projectId = task.projectId || deps.task.projectId || (task.id && getProjectIdFromNodeId(task.id, undefined));
  if (!task.id || !projectId) return Promise.resolve();
  const { state, owns, canRender } = persistenceState(deps);
  const hadQueue = !!state.checklist;
  const queue: NonNullable<PanelPersistenceState['checklist']> = state.checklist ||= { saved: previous.map(i => ({ ...i })), desired: [], ids: new Map() };
  const canonical = (items: ChecklistItem[]) => items.map(i => ({ ...i, id: queue.ids.get(i.id) || i.id }));
  if (hadQueue && state.pending === 0 && !queue.uncertainCreate && sameChecklist(queue.saved, queue.desired)) {
    queue.saved = canonical(previous);
  }
  queue.desired = canonical(desired);
  const base = `/api/v1/projects/${projectId}/tasks/${getNodeId(task.id)}/checklist`;
  return enqueue(deps, async () => {
    // Recompute after every ACK: older snapshots must never overwrite newer intent.
    while (owns()) {
      if (queue.uncertainCreate) throw new Error('Checklist creation was not confirmed. Refresh before retrying to avoid duplicates.');
      queue.desired = canonical(queue.desired);
      const added = queue.desired.find(i => !queue.saved.some(s => s.id === i.id));
      if (added) {
        const response = await apiClient.post<{ data?: { item_id?: string; task?: Partial<ProjectNode>; node?: Partial<ProjectNode> } }>(base, { text: added.text, checked: !!added.checked }).catch(error => {
          const status = (error as { status?: number })?.status;
          if (!status || status >= 500) queue.uncertainCreate = true;
          throw error;
        });
        const data = response?.data;
        const snapshot = data?.task || data?.node;
        const id = data?.item_id;
        if (!id) {
          queue.uncertainCreate = true;
          throw new Error('Checklist creation was not confirmed. Refresh before retrying to avoid duplicates.');
        }
        const saved = snapshot?.checklist?.find(i => i.id === id);
        // Retain this captured request's returned identity even if its session ended.
        // The guard below prevents further writes or UI updates under another session.
        queue.ids.set(added.id, id);
        queue.saved.push({ ...added, ...saved, id });
        queue.desired = canonical(queue.desired);
        if (!owns()) return;
        // Reconcile identity only; preserve current text, checked state, and deletions.
        if (canRender()) {
          deps.setCurrentChecklist(queue.desired);
          deps.setEditedTask(prev => canRender() ? { ...prev, checklist: canonical(prev.checklist || []) } : prev);
        }
        continue;
      }
      const changed = queue.desired.find(i => { const old = queue.saved.find(s => s.id === i.id); return old && (old.text !== i.text || !!old.checked !== !!i.checked); });
      if (changed) {
        const old = queue.saved.find(s => s.id === changed.id)!;
        const payload: { text?: string; checked?: boolean } = {};
        if (old.text !== changed.text) payload.text = changed.text;
        if (!!old.checked !== !!changed.checked) payload.checked = !!changed.checked;
        await apiClient.put(`${base}/${changed.id}`, payload);
        if (!owns()) return;
        queue.saved = queue.saved.map(i => i.id === changed.id ? { ...changed } : i);
        continue;
      }
      const removed = queue.saved.find(i => !queue.desired.some(d => d.id === i.id));
      if (removed) {
        await apiClient.delete(`${base}/${removed.id}`);
        if (!owns()) return;
        queue.saved = queue.saved.filter(i => i.id !== removed.id);
        continue;
      }
      if (queue.saved.some((i, index) => i.id !== queue.desired[index]?.id)) {
        const ordered = queue.desired.map(i => ({ ...i }));
        state.fields.checklist = ordered;
        const revision = ++state.revision;
        state.fieldVersions.checklist = revision;
        await deps.updateNode(`${projectId}:${getNodeId(task.id!)}`, { checklist: ordered });
        state.submittedVersions.checklist = revision;
        if (!owns()) return;
        queue.saved = ordered;
        if (deps.readSavedTask) {
          const confirmed = await deps.readSavedTask(projectId, getNodeId(task.id!));
          if (!owns()) return;
          const confirmedProject = confirmed.projectId || (confirmed.id && getProjectIdFromNodeId(confirmed.id, undefined));
          if (!confirmed.id || getNodeId(confirmed.id) !== getNodeId(task.id!) || confirmedProject !== projectId || !sameChecklist(confirmed.checklist || [], ordered)) {
            throw new Error('Checklist order is not yet confirmed by the server.');
          }
          if (state.fieldVersions.checklist === revision) {
            delete state.fields.checklist;
            delete state.fieldVersions.checklist;
            delete state.submittedVersions.checklist;
          }
        }
        continue;
      }
      if (!Object.keys(state.fields).length) {
        state.error = null;
        if (canRender()) {
          deps.setSaveError?.(null);
          if (state.pending === 1 && !deps.titleDraftStateRef?.current.active) deps.setHasChanges(false);
        }
      }
      return;
    }
  });
}


/** Call only for a server-origin snapshot diff, never an optimistic context echo.
 * Resolving updateNode is not proof of persistence (it can enqueue offline).
 * Returns whether retained writes are clear; the caller still owns draft state.
 */
export function acknowledgePanelPersistence(
  deps: HandlerDependencies,
  confirmed: Partial<ProjectNode>,
  evidence: { source: 'server'; previous: Partial<ProjectNode> },
): boolean {
  if (deps.ownsSession?.() === false || evidence.source !== 'server') return false;
  const { state, canRender } = persistenceState(deps);
  const current = deps.getEditedTask?.() || deps.editedTask;
  const project = (node: Partial<ProjectNode>) => node.projectId || (node.id && getProjectIdFromNodeId(node.id, undefined));
  const sameOwner = (node: Partial<ProjectNode>) => !!node.id && !!current.id && getNodeId(node.id) === getNodeId(current.id) && project(node) === project(current);
  if (!canRender() || state.pending > 0 || !sameOwner(confirmed) || !sameOwner(evidence.previous)) return false;
  const equal = (left: unknown, right: unknown) => JSON.stringify(left) === JSON.stringify(right);
  for (const field of Object.keys(state.fields) as (keyof ProjectNode)[]) {
    if (state.submittedVersions[field] !== state.fieldVersions[field]) continue;
    if (equal(confirmed[field], evidence.previous[field]) || !equal(confirmed[field], state.fields[field])) continue;
    delete state.fields[field];
    delete state.fieldVersions[field];
    delete state.submittedVersions[field];
  }
  const queue = state.checklist;
  if (queue && !queue.uncertainCreate && sameChecklist(queue.saved, queue.desired) && sameChecklist(confirmed.checklist || [], queue.desired)) state.checklist = undefined;
  const clear = !Object.keys(state.fields).length && !state.checklist;
  if (clear) { state.error = null; deps.setSaveError?.(null); }
  return clear;
}

function sameChecklist(left: ChecklistItem[], right: ChecklistItem[]) {
  return left.length === right.length && left.every((item, index) => {
    const other = right[index];
    return item.id === other.id && item.text === other.text && !!item.checked === !!other.checked;
  });
}
